/**
 * @fileoverview Provedor de configuração do React Query para gerenciamento de estado assíncrono.
 * Configura estratégias de cache, retry e refetch para otimizar requisições de API.
 * 
 * @module QueryProvider
 * @requires @tanstack/react-query
 * @since 1.0.0
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

/**
 * Cliente React Query configurado com estratégias otimizadas:
 * - Queries: 3 tentativas com backoff exponencial, cache de 10min, stale de 5min
 * - Mutations: 1 tentativa de retry
 * - Refetch desabilitado no foco da janela, habilitado na reconexão
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * Provedor React Query para gerenciamento de estado assíncrono.
 * Inclui DevTools em modo de desenvolvimento.
 * 
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componentes filhos
 * @returns {JSX.Element} Provedor configurado com QueryClient
 * 
 * @example
 * <QueryProvider>
 *   <App />
 * </QueryProvider>
 */
export const QueryProvider = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default QueryProvider;